// Plain HTTP fetch helpers for Firebase Realtime Database.
// Used on the DANCER side so we don't open a websocket per device — that would
// hit Spark plan's 100 concurrent connection cap at scale.

import { databaseURL } from './config.js'

function url(path, query = '') {
  const clean = path.replace(/^\/+/, '')
  return `${databaseURL}/${clean}.json${query ? `?${query}` : ''}`
}

export async function fetchNode(path, { signal } = {}) {
  const res = await fetch(url(path), { signal })
  if (!res.ok) throw new Error(`Firebase GET ${path} failed: ${res.status}`)
  return res.json()
}

export async function patchNode(path, body, { signal } = {}) {
  const res = await fetch(url(path), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) throw new Error(`Firebase PATCH ${path} failed: ${res.status}`)
  return res.json()
}

export async function pushNode(path, body, { signal } = {}) {
  const res = await fetch(url(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) throw new Error(`Firebase POST ${path} failed: ${res.status}`)
  const data = await res.json()
  return data.name
}

export async function putNode(path, body, { signal } = {}) {
  const res = await fetch(url(path), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) throw new Error(`Firebase PUT ${path} failed: ${res.status}`)
  return res.json()
}

export function deleteNode(path, opts) {
  return putNode(path, null, opts)
}

export function pollNode(path, intervalMs, onData, onError) {
  let stopped = false
  let controller = null
  let timer = null

  async function tick() {
    if (stopped) return
    controller = new AbortController()
    try {
      const data = await fetchNode(path, { signal: controller.signal })
      if (!stopped) onData(data)
    } catch (err) {
      if (!stopped && err.name !== 'AbortError') onError?.(err)
    } finally {
      if (!stopped) timer = setTimeout(tick, intervalMs)
    }
  }
  tick()

  return () => {
    stopped = true
    controller?.abort()
    if (timer) clearTimeout(timer)
  }
}
